/**
 * FrictionlessOnboarding.js
 * 
 * New frictionless onboarding flow:
 * 1. Let users log meals WITHOUT sign-up
 * 2. Show value first, then encourage personalization
 * 3. Sign-up only after value is demonstrated
 * 
 * Flow: Log → See value → Personalize → Save
 */

import React, { useState, useEffect, useRef } from 'react';
import { Camera, Upload, Edit3, ArrowRight, Check, X, ChevronRight, Sparkles, RotateCcw } from 'lucide-react';
import { PrimaryBrandLogo, SmartPlAIteWordmark } from '../brand/BrandLogo';
import { 
  brand, 
  getPrimaryButtonStyle, 
  getSecondaryButtonStyle,
  getGradientTextStyle 
} from '../../styles/brand';
import { CONFIDENCE_FACTOR_CONFIG } from '../miq/MIQView';

const { colors, gradients, typography, radius, shadows, spacing, safeArea } = brand;

// iOS detection helper
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isIOSUA = /iPad|iPhone|iPod/.test(ua);
  const isIOSPlatform = /iPad|iPhone|iPod/.test(platform);
  const isIPadOS13 = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIOSUA || isIOSPlatform || isIPadOS13;
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN 1: FIRST APP LOAD - No sign-in required
// ═══════════════════════════════════════════════════════════════════════════════
export const GuestLandingScreen = ({ 
  onTakePhoto, 
  onUploadPhoto,
  onImageSelected, // NEW: callback when image is selected from file picker
  onManualEntry 
}) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null); // iOS camera file input
  const [previewImage, setPreviewImage] = useState(null); // Image preview before analysis
  const [cameraPermissionRequested, setCameraPermissionRequested] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false); // Local analyzing state for UI feedback
  const [useIOSFileInput, setUseIOSFileInput] = useState(false);
  
  // Check if we should use iOS file input approach
  useEffect(() => {
    setUseIOSFileInput(isIOS());
  }, []);
  
  // Request camera permission early when component mounts (non-iOS only)
  useEffect(() => {
    const requestCameraPermission = async () => {
      if (cameraPermissionRequested || useIOSFileInput) return;
      
      try {
        // Check if mediaDevices is available
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          setCameraPermissionRequested(true);
          
          // Request camera permission with a minimal stream request
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' } 
          });
          // Immediately stop the stream - we just wanted the permission
          stream.getTracks().forEach(track => track.stop());
        }
      } catch (error) {
        // Permission denied or camera not available - that's okay
        console.log('[GuestLanding] Camera permission request:', error.name);
        setCameraPermissionRequested(true);
      }
    };
    
    // Request after a brief delay to let UI settle (non-iOS only)
    if (!useIOSFileInput) {
      const timer = setTimeout(requestCameraPermission, 500);
      return () => clearTimeout(timer);
    }
  }, [cameraPermissionRequested, useIOSFileInput]);
  
  // Handle file selection from the hidden input (for Upload Photo)
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      // Convert to base64 and show preview first
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setPreviewImage(base64); // Show preview instead of navigating immediately
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };
  
  // Handle iOS camera file input (for Take Photo on iOS)
  const handleIOSCameraCapture = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      console.log('[GuestLanding] iOS camera capture received:', file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setPreviewImage(base64);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };
  
  // Handle Take Photo button click
  const handleTakePhotoClick = () => {
    if (useIOSFileInput) {
      // iOS: Use file input with camera capture
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    } else {
      // Non-iOS: Use the native camera flow
      onTakePhoto();
    }
  };
  
  // Trigger the hidden file input when Upload Photo is clicked
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Proceed with the selected image
  const handleProceedWithImage = () => {
    if (previewImage && onImageSelected) {
      setIsAnalyzing(true); // Show loading state
      onImageSelected(previewImage);
    }
  };
  
  // Cancel and choose a different image
  const handleCancelPreview = () => {
    setPreviewImage(null);
  };

  // If we have a preview image, show the preview screen
  if (previewImage) {
    return (
      <div style={{
        minHeight: '100vh',
        background: gradients.background,
        padding: '40px 20px 100px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <SmartPlAIteWordmark fontSize={24} />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '340px' }}>
          <h2 style={{
            fontSize: '22px', fontWeight: '300', color: colors.text,
            margin: '0 0 8px 0', fontFamily: typography.headingFont, letterSpacing: '-0.01em'
          }}>
            Ready to analyze?
          </h2>
          <p style={{
            fontSize: '13px', color: colors.textSecondary, margin: 0, fontFamily: typography.fontFamily
          }}>
            MIQ will identify the food and estimate nutrition
          </p>
        </div>

        <div style={{
          width: '100%', maxWidth: '340px', marginBottom: '24px',
          borderRadius: radius.xl, overflow: 'hidden',
          border: `1px solid ${colors.accentBorder}`,
          boxShadow: shadows.glow
        }}>
          <img 
            src={previewImage} 
            alt="Food preview" 
            data-testid="guest-image-preview"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '300px',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{
          width: '100%',
          maxWidth: '340px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {/* Primary: Analyze */}
          <button
            data-testid="guest-analyze-preview"
            onClick={handleProceedWithImage}
            disabled={isAnalyzing}
            style={{
              width: '100%',
              minHeight: '56px',
              padding: '18px 24px',
              background: isAnalyzing 
                ? 'rgba(124, 92, 252, 0.5)' 
                : gradients.primary,
              border: 'none',
              borderRadius: radius.lg,
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              fontFamily: typography.headingFont,
              letterSpacing: '0.02em',
              cursor: isAnalyzing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: shadows.glow,
              transition: 'transform 0.2s',
              opacity: isAnalyzing ? 0.8 : 1
            }}
          >
            {isAnalyzing ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Analyze This Meal
              </>
            )}
          </button>

          {/* Secondary: Choose Different */}
          <button
            data-testid="guest-choose-different"
            onClick={handleUploadClick}
            disabled={isAnalyzing}
            style={{
              width: '100%',
              minHeight: '48px', // iOS/Android tap target
              padding: '14px 24px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '12px',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isAnalyzing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isAnalyzing ? 0.5 : 1,
              gap: '8px'
            }}
          >
            <RotateCcw size={16} />
            Choose Different Photo
          </button>

          {/* Cancel: Go Back */}
          <button
            data-testid="guest-cancel-preview"
            onClick={handleCancelPreview}
            disabled={isAnalyzing}
            style={{
              width: '100%',
              minHeight: '44px', // iOS/Android tap target
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isAnalyzing ? 'not-allowed' : 'pointer',
              padding: '12px 16px',
              opacity: isAnalyzing ? 0.5 : 1
            }}
          >
            Cancel
          </button>
        </div>

        {/* Hidden file input for re-selection */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh', // Dynamic viewport height for iOS Safari
      background: gradients.background,
      padding: `${spacing.s9} ${spacing.s5} calc(${spacing.s10} + ${safeArea.paddingBottom}) ${spacing.s5}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle glow effect behind hero */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '120%',
        height: '50%',
        background: gradients.glow,
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        data-testid="guest-upload-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleIOSCameraCapture}
        style={{ display: 'none' }}
        data-testid="guest-ios-camera-input"
      />
      
      {/* ══════════════════════════════════════════════════════════════════
          LOGO - Primary Brand Logo (Plate + Wordmark)
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{ marginBottom: spacing.s8, zIndex: 1 }}>
        <PrimaryBrandLogo size="large" showTagline={true} />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          HERO - Headline + Subtext
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        textAlign: 'center',
        marginBottom: spacing.s8,
        maxWidth: '340px',
        zIndex: 1,
      }}>
        <h1 style={{
          ...typography.h1,
          fontFamily: typography.headingFont,
          fontWeight: '300',
          color: colors.text,
          margin: `0 0 ${spacing.s4} 0`,
        }}>
          Come for Calorie Tracking.<br/>
          <span style={getGradientTextStyle()}>
            Stay for Intelligence.
          </span>
        </h1>
        <p style={{
          ...typography.body,
          fontFamily: typography.fontFamily,
          color: colors.textSecondary,
          margin: 0,
          fontWeight: '400',
        }}>
          Log meals your way — Health Intelligence helps immediately and gets smarter over time.
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          CTA STACK - Primary + Secondary buttons
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        width: '100%',
        maxWidth: '340px',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.s3,
        zIndex: 1,
      }}>
        {/* Take Photo - Primary CTA */}
        <button
          data-testid="guest-take-photo"
          onClick={handleTakePhotoClick}
          style={getPrimaryButtonStyle({ fullWidth: true })}
        >
          <Camera size={20} />
          Take Photo
        </button>

        {/* Secondary Row - Upload Photo & Manual Entry */}
        <div style={{ display: 'flex', gap: spacing.s3 }}>
          <button
            data-testid="guest-upload-photo"
            onClick={handleUploadClick}
            style={getSecondaryButtonStyle()}
          >
            <Upload size={18} />
            Upload Photo
          </button>

          <button
            data-testid="guest-manual-entry"
            onClick={onManualEntry}
            style={getSecondaryButtonStyle()}
          >
            <Edit3 size={18} />
            Manual Entry
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          SIGN IN LINK
      ══════════════════════════════════════════════════════════════════ */}
      <button
        data-testid="guest-sign-in-link"
        onClick={() => window.dispatchEvent(new CustomEvent('showSignIn'))}
        style={{
          marginTop: spacing.s7,
          padding: `${spacing.s3} ${spacing.s5}`,
          minHeight: '44px', // iOS tap target
          background: 'none',
          border: 'none',
          color: colors.mutedText,
          fontSize: typography.bodySmall.fontSize,
          fontFamily: typography.fontFamily,
          cursor: 'pointer',
          zIndex: 1,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        Already have an account?{' '}
        <span style={{ color: colors.accent, fontWeight: '500' }}>Sign in</span>
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN 2: POST-MEAL RESULT (First Log)
// ═══════════════════════════════════════════════════════════════════════════════
export const FirstMealSuccessSheet = ({ 
  mealData,
  onPersonalize, 
  onLogAnother,
  onDismiss 
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'flex-end',
      zIndex: 1000
    }}>
      <div style={{
        width: '100%',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
        borderRadius: '24px 24px 0 0',
        padding: '32px 24px 60px 24px', // Extra bottom padding for safe area
        maxHeight: '85vh',
        overflow: 'auto',
        position: 'relative' // For absolute positioning of close button
      }}>
        {/* Close button */}
        <button
          data-testid="first-meal-success-close"
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '44px', // iOS/Android tap target
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.6)'
          }}
        >
          <X size={20} />
        </button>

        {/* Success Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)'
        }}>
          <Check size={32} color="white" />
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: 'white',
          textAlign: 'center',
          margin: '0 0 8px 0'
        }}>
          Logged.
        </h2>

        {/* Reinforcement */}
        <p style={{
          fontSize: '15px',
          color: 'rgba(255,255,255,0.7)',
          textAlign: 'center',
          margin: '0 0 8px 0'
        }}>
          MetaBolicIQ (MIQ) 🧠 is now learning from your meals.
        </p>
        <p style={{
          fontSize: '13px',
          color: 'rgba(255,255,255,0.5)',
          textAlign: 'center',
          margin: '0 0 24px 0'
        }}>
          You don&apos;t need perfect data — MIQ improves as patterns emerge.
        </p>

        {/* Meal Summary (if available) */}
        {mealData && (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>This meal</span>
              <span style={{ color: 'white', fontWeight: '600', fontSize: '18px' }}>
                {mealData.calories || '~'} kcal
              </span>
            </div>
            {/* Always show macros if mealData exists */}
            <div style={{
              display: 'flex',
              gap: '16px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.5)'
            }}>
              <span>P: {mealData.protein_g || 0}g</span>
              <span>C: {mealData.carbs_g || 0}g</span>
              <span>F: {mealData.fat_g || 0}g</span>
            </div>
          </div>
        )}

        {/* Primary CTA - Create Free Account */}
        <button
          data-testid="create-account-btn"
          onClick={onPersonalize}
          style={{
            width: '100%',
            minHeight: '52px',
            padding: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          Create Free Account
        </button>

        {/* Secondary CTA - Continue to Explore */}
        <button
          data-testid="continue-explore-btn"
          onClick={onLogAnother}
          style={{
            width: '100%',
            minHeight: '44px',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '12px',
            color: 'rgba(255,255,255,0.8)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '12px 16px'
          }}
        >
          Continue to Explore
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN 3: PROFILE / TARGET SETUP (Optional but encouraged)
// ═══════════════════════════════════════════════════════════════════════════════
export const PersonalizeTargetsModal = ({ 
  onComplete, 
  onSkip,
  onClose 
}) => {
  const [step, setStep] = useState(1); // 1: Goal, 2: Body, 3: Activity
  const [useImperial, setUseImperial] = useState(false); // Metric by default
  const [formData, setFormData] = useState({
    goal: '',
    height: '',
    heightFt: '',
    heightIn: '',
    weight: '',
    age: '',
    gender: '',
    activityLevel: ''
  });

  const handleGoalSelect = (goal) => {
    setFormData(prev => ({ ...prev, goal }));
    setStep(2);
  };

  const handleBodyDetailsSubmit = () => {
    // Convert imperial to metric for storage if needed
    let heightCm = formData.height;
    let weightKg = formData.weight;
    
    if (useImperial) {
      // Convert feet/inches to cm
      const ft = parseFloat(formData.heightFt) || 0;
      const inches = parseFloat(formData.heightIn) || 0;
      heightCm = Math.round((ft * 12 + inches) * 2.54);
      // Convert lbs to kg
      weightKg = Math.round(parseFloat(formData.weight) * 0.453592);
    }
    
    if (heightCm && weightKg && formData.age && formData.gender) {
      setFormData(prev => ({ 
        ...prev, 
        height: heightCm.toString(),
        weight: weightKg.toString()
      }));
      setStep(3);
    }
  };

  const handleActivitySelect = (level) => {
    const finalData = { ...formData, activityLevel: level, useImperial };
    onComplete(finalData);
  };
  
  // Validation for body details
  const isBodyDetailsValid = useImperial 
    ? (formData.heightFt && formData.weight && formData.age && formData.gender)
    : (formData.height && formData.weight && formData.age && formData.gender);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
        borderRadius: '24px',
        padding: '32px 24px 40px 24px', // Extra bottom padding for safe area
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative' // For absolute positioning of close button
      }}>
        {/* Close button */}
        <button
          data-testid="personalize-modal-close"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '44px', // iOS/Android tap target
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.6)'
          }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <h2 style={{
          fontSize: '22px',
          fontWeight: '700',
          color: 'white',
          margin: '0 0 8px 0',
          textAlign: 'center'
        }}>
          Make this yours
        </h2>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.6)',
          margin: '0 0 24px 0',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          A few details help MetaBolicIQ (MIQ) 🧠 tailor calories, macros, and timing to your body.
        </p>

        {/* Progress indicator */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          justifyContent: 'center'
        }}>
          {[1, 2, 3].map(s => (
            <div
              key={s}
              style={{
                width: '32px',
                height: '4px',
                borderRadius: '2px',
                background: s <= step ? '#667eea' : 'rgba(255,255,255,0.2)'
              }}
            />
          ))}
        </div>

        {/* Step 1: Goal */}
        {step === 1 && (
          <div>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
              What&apos;s your primary goal?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { id: 'lose', label: 'Lose weight', desc: 'Create a sustainable calorie deficit' },
                { id: 'maintain', label: 'Maintain weight', desc: 'Stay balanced with your current intake' },
                { id: 'gain', label: 'Build muscle', desc: 'Support muscle growth with more fuel' }
              ].map(option => (
                <button
                  key={option.id}
                  data-testid={`goal-${option.id}`}
                  onClick={() => handleGoalSelect(option.id)}
                  style={{
                    minHeight: '64px', // iOS/Android tap target
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{option.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Body Details */}
        {step === 2 && (
          <div>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
              Basic body details
            </p>
            
            {/* Metric/Imperial Toggle */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '8px',
                padding: '4px'
              }}>
                <button
                  type="button"
                  onClick={() => setUseImperial(false)}
                  style={{
                    padding: '8px 16px',
                    background: !useImperial ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: !useImperial ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Metric (cm/kg)
                </button>
                <button
                  type="button"
                  onClick={() => setUseImperial(true)}
                  style={{
                    padding: '8px 16px',
                    background: useImperial ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: useImperial ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Imperial (ft/lb)
                </button>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Height Input - changes based on unit system */}
              {useImperial ? (
                <>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      placeholder="Feet"
                      value={formData.heightFt}
                      onChange={(e) => setFormData(prev => ({ ...prev, heightFt: e.target.value }))}
                      style={{
                        flex: 1,
                        padding: '14px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                    <input
                      type="number"
                      placeholder="Inches"
                      value={formData.heightIn}
                      onChange={(e) => setFormData(prev => ({ ...prev, heightIn: e.target.value }))}
                      style={{
                        flex: 1,
                        padding: '14px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <input
                    type="number"
                    placeholder="Weight (lb)"
                    value={formData.weight}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </>
              ) : (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="number"
                    placeholder="Height (cm)"
                    value={formData.height}
                    onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Weight (kg)"
                    value={formData.weight}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="number"
                  placeholder="Age"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: formData.gender ? 'white' : 'rgba(255,255,255,0.4)',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Sex</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <button
                onClick={handleBodyDetailsSubmit}
                disabled={!isBodyDetailsValid}
                style={{
                  minHeight: '52px', // iOS/Android tap target
                  padding: '14px',
                  background: isBodyDetailsValid
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: isBodyDetailsValid ? 'pointer' : 'not-allowed',
                  marginTop: '8px'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Activity Level */}
        {step === 3 && (
          <div>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
              How active are you?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { id: 'sedentary', label: 'Sedentary', desc: 'Little to no exercise' },
                { id: 'light', label: 'Lightly active', desc: 'Light exercise 1-3 days/week' },
                { id: 'moderate', label: 'Moderately active', desc: 'Moderate exercise 3-5 days/week' },
                { id: 'active', label: 'Very active', desc: 'Hard exercise 6-7 days/week' }
              ].map(option => (
                <button
                  key={option.id}
                  data-testid={`activity-${option.id}`}
                  onClick={() => handleActivitySelect(option.id)}
                  style={{
                    minHeight: '56px', // iOS/Android tap target
                    padding: '14px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'white',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '2px', fontSize: '14px' }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{option.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Skip link */}
        <button
          data-testid="personalize-skip"
          onClick={onSkip}
          style={{
            width: '100%',
            minHeight: '44px', // iOS/Android tap target
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '16px',
            textAlign: 'center'
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN 4: TARGETS UPDATED (Aha Moment)
// ═══════════════════════════════════════════════════════════════════════════════
export const TargetsUpdatedSheet = ({ 
  calculatedTargets,
  onSmartGoals,
  onSaveProgress, 
  onContinueWithoutSaving,
  onDismiss 
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
        borderRadius: '24px',
        padding: '32px 24px 40px 24px', // Extra bottom padding for safe area
        textAlign: 'center',
        position: 'relative' // For absolute positioning of close button
      }}>
        {/* Close button */}
        {onDismiss && (
          <button
            data-testid="targets-updated-close"
            onClick={onDismiss}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '44px', // iOS/Android tap target
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.6)'
            }}
          >
            <X size={20} />
          </button>
        )}

        {/* Success Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}>
          <Sparkles size={28} color="white" />
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '22px',
          fontWeight: '700',
          color: 'white',
          margin: '0 0 12px 0'
        }}>
          Your targets just got smarter
        </h2>

        {/* Supporting text */}
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.7)',
          margin: '0 0 8px 0',
          lineHeight: '1.5'
        }}>
          These numbers are now personalized to your body and goals — not generic averages.
        </p>
        
        {/* MIQ Progress indicator */}
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>🧠</span>
          <span style={{ fontSize: '12px', color: '#10B981', fontWeight: '500' }}>
            MIQ Confidence: 1/{CONFIDENCE_FACTOR_CONFIG?.TOTAL_FACTORS || 9} factors active
          </span>
        </div>

        {/* Calculated Targets (if available) */}
        {calculatedTargets && (
          <div style={{
            background: 'rgba(102, 126, 234, 0.1)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#667eea',
              marginBottom: '8px'
            }}>
              {calculatedTargets.calories || '~'} kcal/day
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.6)'
            }}>
              <span>P: {calculatedTargets.protein || '~'}g</span>
              <span>C: {calculatedTargets.carbs || '~'}g</span>
              <span>F: {calculatedTargets.fat || '~'}g</span>
            </div>
          </div>
        )}

        {/* Primary CTA - SmartGoals */}
        <button
          data-testid="setup-smartgoals-btn"
          onClick={onSmartGoals}
          style={{
            width: '100%',
            minHeight: '52px', // iOS/Android tap target
            padding: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <span>🧠</span>
          <span>Set Up SmartGoals</span>
        </button>
        
        {/* Info text */}
        <p style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.4)',
          margin: '0 0 16px 0'
        }}>
          Fine-tune your goals with AI for even better recommendations
        </p>

        {/* Secondary CTA */}
        <button
          data-testid="continue-without-saving"
          onClick={onContinueWithoutSaving}
          style={{
            width: '100%',
            minHeight: '44px', // iOS/Android tap target
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '12px 16px'
          }}
        >
          Continue to dashboard
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN 5: OPTIONAL SIGN-UP (Value-Framed)
// ═══════════════════════════════════════════════════════════════════════════════
export const SignUpPromptSheet = ({ 
  onCreateAccount, 
  onSkip,
  onDismiss 
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
        borderRadius: '24px',
        padding: '32px 24px 40px 24px', // Extra bottom padding for safe area
        textAlign: 'center',
        position: 'relative' // For absolute positioning of close button
      }}>
        {/* Close button */}
        <button
          data-testid="signup-prompt-close"
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '44px', // iOS/Android tap target
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.6)'
          }}
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div style={{
          width: '56px',
          height: '56px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}>
          <span style={{ fontSize: '24px' }}>🧠</span>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '22px',
          fontWeight: '700',
          color: 'white',
          margin: '0 0 8px 0'
        }}>
          Save your progress
        </h2>

        {/* Subheadline */}
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.7)',
          margin: '0 0 20px 0',
          lineHeight: '1.5'
        }}>
          Create a free account to keep your meals, targets, and experience full MIQ features during your free trial.
        </p>

        {/* Benefits */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          textAlign: 'left'
        }}>
          {[
            { icon: '✓', text: 'Tracking is always free', highlight: true },
            { icon: '✓', text: 'Your meals and targets are preserved' },
            { icon: '🧠', text: '30-day Intelligence Calibration Trial' },
            { icon: '📊', text: 'Personalized insights over time' }
          ].map((benefit, idx) => (
            <div 
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 0',
                borderBottom: idx < 3 ? '1px solid rgba(255,255,255,0.05)' : 'none'
              }}
            >
              <span style={{ 
                color: benefit.highlight ? '#10B981' : '#667eea',
                fontSize: '14px',
                width: '20px',
                textAlign: 'center'
              }}>
                {benefit.icon}
              </span>
              <span style={{ 
                color: benefit.highlight ? '#10B981' : 'rgba(255,255,255,0.8)', 
                fontSize: '14px',
                fontWeight: benefit.highlight ? '600' : '400'
              }}>
                {benefit.text}
              </span>
            </div>
          ))}
        </div>
        
        {/* What happens after trial */}
        <p style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.4)',
          margin: '0 0 20px 0',
          textAlign: 'center'
        }}>
          After your calibration trial, basic tracking stays free. Premium features require a subscription.
        </p>

        {/* Primary CTA */}
        <button
          data-testid="create-account-btn"
          onClick={onCreateAccount}
          style={{
            width: '100%',
            minHeight: '52px', // iOS/Android tap target
            padding: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '12px'
          }}
        >
          Create free account
        </button>

        {/* Skip */}
        <button
          data-testid="signup-skip"
          onClick={onSkip}
          style={{
            width: '100%',
            minHeight: '44px', // iOS/Android tap target
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '12px 16px'
          }}
        >
          Continue as guest
        </button>
      </div>
    </div>
  );
};

export default {
  GuestLandingScreen,
  FirstMealSuccessSheet,
  PersonalizeTargetsModal,
  TargetsUpdatedSheet,
  SignUpPromptSheet
};
